import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Send, Loader2, CheckCircle2, Sparkles, IndianRupee } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function PublicChat() {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.post(`/public/automation/${slug}/start`).then((r) => {
      setAgent(r.data.agent);
      setSessionId(r.data.session_id);
      setMessages(r.data.messages);
      setPayments(r.data.payments || null);
    }).catch((e) => setError(e?.response?.data?.detail || "Agent not found")).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    if (!text.trim() || sending || complete) return;
    const trimmed = text.trim();
    setText("");
    setSending(true);
    setMessages((m) => [...m, { id: "tmp-" + Date.now(), role: "customer", text: trimmed, _tmp: true }]);
    try {
      const r = await api.post(`/public/automation/${slug}/message`, { session_id: sessionId, text: trimmed });
      setMessages((m) => [...m.filter((x) => !x._tmp), ...r.data.messages]);
      if (r.data.complete) setComplete(true);
    } catch (e) {
      setMessages((m) => m.filter((x) => !x._tmp));
      setText(trimmed);
    } finally { setSending(false); }
  };

  const payAdvance = async () => {
    if (paying || paid) return;
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Payment gateway blocked");
      const orderRes = await api.post(`/public/automation/${slug}/pay/order`, { session_id: sessionId });
      const captured = messages.reduce((acc, m) => acc, {}); // not used; Razorpay prefill optional
      const rzp = new window.Razorpay({
        key: orderRes.data.key_id,
        amount: orderRes.data.amount_paise,
        currency: orderRes.data.currency,
        order_id: orderRes.data.order_id,
        name: orderRes.data.business_name || agent?.business_name || "ipnova AI",
        description: orderRes.data.label,
        theme: { color: "#10b981" },
        handler: async (res) => {
          try {
            const v = await api.post(`/public/automation/${slug}/pay/verify`, {
              session_id: sessionId,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setPaid(v.data.payment);
            toast.success("Payment received");
          } catch {
            toast.error("Payment verification failed. Please contact the business.");
          } finally { setPaying(false); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment. Try again.");
      setPaying(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black grid place-items-center"><Loader2 className="w-6 h-6 text-zinc-500 animate-spin"/></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="font-display text-3xl tracking-tighter mb-2">Agent unavailable</div>
          <div className="text-zinc-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="public-chat">
      <header className="glass border-b border-white/10 px-5 h-16 flex items-center gap-3 sticky top-0 z-30">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center">
          <Bot className="w-5 h-5 text-black" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display tracking-tight truncate">{agent?.business_name || agent?.name}</div>
          <div className="text-[11px] text-emerald-400 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot"/> Online · AI assistant
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-3 sm:px-6 py-6 max-w-3xl mx-auto w-full" ref={scrollRef}>
        <div className="space-y-3">
          {messages.map((m) => <Bubble key={m.id} m={m} />)}
          {sending && <TypingBubble />}
          {complete && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-emerald-500/[0.06] border border-emerald-500/30 rounded-xl p-4 mt-4 text-sm flex items-start gap-3" data-testid="chat-complete">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0"/>
              <div className="flex-1">
                <div className="font-display tracking-tight">All set!</div>
                <div className="text-zinc-400 text-xs mt-0.5">Your inquiry has been received. The team will reach out to you shortly.</div>
              </div>
            </motion.div>
          )}
          {complete && payments?.enabled && !paid && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-3 bg-black border border-emerald-500/40 rounded-xl p-4" data-testid="pay-advance-card">
              <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 mb-1 flex items-center gap-2">
                <IndianRupee className="w-3 h-3"/> Confirm your booking
              </div>
              <div className="font-display text-xl tracking-tight">Pay ₹{payments.advance_amount} advance</div>
              <div className="text-xs text-zinc-500 mt-1">{payments.payment_label} · Powered by Razorpay {payments.mode === "test" ? "(test mode)" : ""}</div>
              <button onClick={payAdvance} disabled={paying}
                className="mt-3 w-full h-11 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
                data-testid="pay-advance-btn">
                {paying ? <><Loader2 className="w-4 h-4 animate-spin"/> Opening secure checkout…</> : <>Pay ₹{payments.advance_amount} now</>}
              </button>
            </motion.div>
          )}
          {paid && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-3 bg-emerald-500/[0.08] border border-emerald-500/50 rounded-xl p-4 text-sm" data-testid="paid-card">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400"/>
                <div className="font-display tracking-tight">Payment received · ₹{paid.amount_inr}</div>
              </div>
              <div className="text-[11px] text-zinc-400 mt-1 font-mono truncate">{paid.razorpay_payment_id}</div>
            </motion.div>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 px-3 sm:px-6 py-4 bg-black sticky bottom-0">
        <div className="max-w-3xl mx-auto flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            disabled={sending || complete}
            placeholder={complete ? "Conversation completed" : "Type your reply…"}
            className="flex-1 bg-zinc-950 border border-white/10 rounded-full px-4 h-11 focus:border-emerald-500/40 outline-none text-sm disabled:opacity-50"
            data-testid="public-chat-input"
            autoFocus
          />
          <button onClick={send} disabled={sending || complete || !text.trim()}
            className="w-11 h-11 rounded-full bg-emerald-500 text-black hover:bg-emerald-400 grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="public-chat-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          </button>
        </div>
        <div className="mt-2 text-center text-[10px] text-zinc-600 flex items-center justify-center gap-1">
          <Sparkles className="w-3 h-3"/> Powered by ipnova.ai
        </div>
      </footer>
    </div>
  );
}

function Bubble({ m }) {
  const isAi = m.role === "ai";
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className={`flex ${isAi ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-2.5 border ${isAi ? "bg-zinc-900 border-white/10" : "bg-emerald-500 border-emerald-500 text-black"}`}>
        <div className="text-sm leading-relaxed">{m.text}</div>
      </div>
    </motion.div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "0ms" }}/>
        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "150ms" }}/>
        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "300ms" }}/>
      </div>
    </div>
  );
}
